import { mediaHelper, SITE_HEADER_HEIGHT_PX, spacingHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { PageContainer } from '../../../components/PageContainer';
import { PageHeaderContainer } from '../../../components/PageHeaderContainer';
import { useStickyHeaderContext } from '../../contexts/StickyHeaderContext/StickyHeaderContext';
import {
  StickyState,
  isStickySearchHeaderStuckAndNotFull,
  isStickySearchHeaderStuck,
} from '../../hooks/useStickySearchHeaderState';
import { SearchLandingPageHeaderButtons } from '../SearchLandingPageHeaderButtons/SearchLandingPageHeaderButtons';

const BREADCRUMBS_BAR_HEIGHT = '42px';
const FOOTER_BAR_HEIGHT = spacingHelper('threeExtraLarge');
const TRANSITION_TIME = '300ms';
const TRANSITION_EASING = 'ease-out';

const StickySpacer = styled.div<{ $isSticky: boolean; $height?: number }>`
  height: ${({ $isSticky, $height }) => ($isSticky ? `${$height}px` : '0')};
`;

const HeaderContainer = styled(PageHeaderContainer)<{
  $isSticky: boolean;
  $disableAnimation: boolean;
}>`
  padding-bottom: 0;
  width: 100%;

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: fixed;
      top: ${SITE_HEADER_HEIGHT_PX}px;
      z-index: 75;
    `}

  ${({ $disableAnimation }) =>
    $disableAnimation &&
    css`
      div {
        transition: none;
      }
    `}
`;

const InnerContainer = styled(PageContainer)<{ $stickyState: StickyState }>`
  padding-top: 0;
  padding-bottom: ${FOOTER_BAR_HEIGHT};
  transition: ${TRANSITION_TIME} padding-bottom ${TRANSITION_EASING};
  position: relative;

  ${mediaHelper('tabletMax')} {
    padding-left: ${spacingHelper('large')};
    padding-right: ${spacingHelper('large')};
  }
  ${mediaHelper('mobileSmall')} {
    padding-left: ${spacingHelper('small')};
    padding-right: ${spacingHelper('small')};
  }

  ${({ $stickyState }) => {
    if ($stickyState === 'header-collapsed') {
      return css`
        padding-bottom: ${spacingHelper('large')};
      `;
    }
  }}
`;

const Breadcrumbs = styled.div<{ $isHidden: boolean }>`
  /*
  Approach here is to position the breadcrumbs absolutely in the container
  This enables us to shrink the height while anchoring to the bottom - and wiping top-down
  */
  height: ${BREADCRUMBS_BAR_HEIGHT};
  overflow: hidden;
  position: relative;
  transition: ${TRANSITION_TIME} height ${TRANSITION_EASING};

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      height: 0;
    `}

  > div {
    bottom: 0;
    position: absolute;
    width: 100%;
  }
`;

const Title = styled.div<{ $isHidden: boolean }>`
  margin-bottom: ${spacingHelper('medium')};
  /*
  We transition on max height as you cannot transition from height:auto to height:x
  Mobile requires more height to allow for wrapping
  Translate is here to help with the text to appear to be wiping top-down
  */
  transition:
    ${TRANSITION_TIME} max-height ${TRANSITION_EASING},
    ${TRANSITION_TIME} padding-top ${TRANSITION_EASING};
  padding-top: ${spacingHelper('extraLarge')};
  max-height: 120px;

  ${mediaHelper('mobile')} {
    max-height: 160px;
  }

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      max-height: 0;
      padding-top: 0;

      ${mediaHelper('mobile')} {
        max-height: 0;
      }
    `}

  > div {
    opacity: 1;
    transition:
      ${TRANSITION_TIME} opacity ${TRANSITION_EASING},
      ${TRANSITION_TIME} transform ${TRANSITION_EASING};
    transform: translateY(0%);

    ${({ $isHidden }) =>
      $isHidden &&
      css`
        opacity: 0;
        transform: translateY(-50%);
      `}
  }
`;

const TitleInner = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const MapViewButtonWrapper = styled.div`
  ${mediaHelper('desktopMin')} {
    display: none;
  }
`;

const MapSplitViewButtonWrapper = styled.div`
  max-height: ${spacingHelper('extraLarge')};

  ${mediaHelper('tabletMax')} {
    display: none;
  }
`;

const SearchButtons = styled.div`
  z-index: 1;
  position: relative;
  padding-top: ${spacingHelper('twoExtraSmall')};
`;

const Footer = styled.div<{ $isHidden: boolean }>`
  overflow: hidden;
  max-height: ${FOOTER_BAR_HEIGHT};
  transition: ${TRANSITION_TIME} max-height ${TRANSITION_EASING};

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      max-height: 0;
    `}

  .footer-inner {
    opacity: 1;
    transition: ${TRANSITION_TIME} opacity ${TRANSITION_EASING};

    ${({ $isHidden }) =>
      $isHidden &&
      css`
        opacity: 0;
      `}
  }
`;

const FooterInner = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: absolute;
  bottom: 0;
  width: 100%;
  height: ${FOOTER_BAR_HEIGHT};
  z-index: 0;
  ${mediaHelper('tabletMax')} {
    width: calc(100% - ${spacingHelper('large')}*2);
  }
  ${mediaHelper('mobileSmall')} {
    width: calc(100% - ${spacingHelper('small')}*2);
  }
`;

export const SearchLandingPageStickyHeader: FunctionComponent<{
  title: React.ReactNode;
  sorting: React.ReactNode;
  mapViewButton?: React.ReactNode;
  mapSplitViewButton?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
}> = ({ title, sorting, breadcrumbs, mapViewButton, mapSplitViewButton }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [stickySpacerHeight, setStickySpacerHeight] = useState<number>();
  const { stickyState, previousStickyStatus } = useStickyHeaderContext();
  const isSticky = isStickySearchHeaderStuck(stickyState);
  const isStickyButNotFull = isStickySearchHeaderStuckAndNotFull(stickyState);
  const isCollapsed = stickyState === 'header-collapsed';
  const disableAnimation = previousStickyStatus === 'static-header-full';

  // Update the sticky space r height based on the current header height
  useEffect(() => {
    if (headerRef?.current) {
      setStickySpacerHeight(headerRef.current.offsetHeight);
    }
  }, [headerRef]);

  return (
    <>
      <StickySpacer $height={stickySpacerHeight} $isSticky={isSticky} />
      <HeaderContainer ref={headerRef} $isSticky={isSticky} $disableAnimation={disableAnimation}>
        {breadcrumbs && (
          <Breadcrumbs
            data-testid="search-header-breadcrumb"
            $isHidden={isStickyButNotFull}
            aria-hidden={isStickyButNotFull}
          >
            {breadcrumbs}
          </Breadcrumbs>
        )}

        <InnerContainer $stickyState={stickyState}>
          <Title $isHidden={isStickyButNotFull} aria-hidden={isStickyButNotFull}>
            <TitleInner>
              {title}
              <MapSplitViewButtonWrapper>{mapSplitViewButton}</MapSplitViewButtonWrapper>
            </TitleInner>
          </Title>

          <SearchButtons>
            <SearchLandingPageHeaderButtons />
          </SearchButtons>

          <Footer $isHidden={isCollapsed} aria-hidden={isCollapsed}>
            <FooterInner className="footer-inner">
              {!isCollapsed && (
                <>
                  {sorting}
                  <MapViewButtonWrapper>{mapViewButton}</MapViewButtonWrapper>
                </>
              )}
            </FooterInner>
          </Footer>
        </InnerContainer>
      </HeaderContainer>
    </>
  );
};
